import { forwardRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import Card from "../../common/components/Card";
import ColorPicker from "../../common/components/ColorPicker";
import { Col, Row } from "../../common/components/Grid";
import FormEditor from "../../common/components/GridEditor";
import TextInput from "../../common/components/TextInput";
import useCredentials from "../../common/hooks/credentialsHook";
import useNetwork from "../../common/hooks/networkHook";
import { canCreateSite, canRemoveSite, canUpdateSite, createSite, removeSite, updateSite, useSites } from "../../reducers/siteSlice";
import PropTypes from 'prop-types';
import NumericInput from "../../common/components/NumericInput";
import ReactForm from "../../common/components/ReactForm";
import SimpleSettings from "./SimpleSettings";
import CustomForm from "../../common/components/CustomForm";
import useClientConfig from "../../common/hooks/clientConfigHook";

const defaultSort = ['sequence','name']
const newSiteTemplate = { customfields: [], sequence: 1 };

const SiteLabel = ({ value: s }) => {
    return <Row>
        <Col xs="auto">
            <div style={{ marginLeft: 'auto', width: '40px', height: '18px', backgroundColor: s.color}}></div>
        </Col>
        <Col className="text-left px-2">
            {s.name}
        </Col>
    </Row>
}

const SiteDetails = forwardRef(({ value: site, isNew, disabled, onChange }, ref) => {
    const { t } = useTranslation();
    const clientConfig = useClientConfig();
    return <>
        <ReactForm ref={ref}>
            <Row>
                <Col md={4}>
                    <TextInput placeholder={t('name')} readonly={!isNew} value={site.name} onChange={name => onChange({ name })} required></TextInput>
                </Col>
                <Col md={4}>
                    <ColorPicker placeholder={t('color')} disabled={disabled} value={site.color} onChange={color => onChange({ color })} required></ColorPicker>
                </Col>
                <Col md={4}>
                    <NumericInput placeholder={t('sequence')} disabled={disabled} value={site.sequence} onChange={sequence => onChange({ sequence })} min={1} step={1} required></NumericInput>
                </Col>
            </Row>
            { clientConfig.siteCustomFields && <CustomForm def={clientConfig.siteCustomFields} data={site.customfields} onChange={customfields => onChange({ customfields })} required></CustomForm> }
        </ReactForm>
        <Row>
            <Col>
                <Card title={t('customfields')}>
                    <FormEditor def={site.formdef} disabled={disabled} onChange={formdef => onChange({ formdef })}></FormEditor>
                </Card>
            </Col>
        </Row>
        { clientConfig.enableCrews && <Row>
            <Col>
                <Card title={t('crew_customfields')}>
                    <FormEditor def={site.crew_formdef} disabled={disabled} onChange={crew_formdef => onChange({crew_formdef})} hideAccess></FormEditor>
                </Card>
            </Col>
        </Row> }
    </>
})

const Site = ({ uuid, label, onClose }) => {
    const { t } = useTranslation();
    const credentials = useCredentials();
    const network = useNetwork();
    const sites = useSites();
    const sortFields = useMemo(() => ({
        sequence: t('sequence'),
        name: t('name')
    }), [t]);

    return <SimpleSettings data={sites} uuid={uuid} label={label} pkey="name" defaultSort={defaultSort} sortFields={sortFields}
        templateItem={newSiteTemplate}
        canAdd={site => canCreateSite(credentials) && sites.every(s => s.name !== site.name)}
        canUpdate={site => canUpdateSite(credentials, site) && sites.filter(s => s.name === site.name).length === 1}
        canRemove={site => canRemoveSite(credentials, site)}
        onAdd={newSite => createSite(network, newSite)}
        onChange={(site, delta) => updateSite(network, { name: site.name, ...delta })}
        onRemove={(site) => removeSite(network, site)} ItemLabel={SiteLabel} ItemDetails={SiteDetails}
        onClose={onClose}></SimpleSettings>
}
Site.propTypes = {
    /**
     * ID to identify the settings status entry
     */
    uuid: PropTypes.string.isRequired,
    /**
     * Label for the screen
     */
    label: PropTypes.string.isRequired,
    /**
     * Function to call when the screen is closed
     */
    onClose: PropTypes.func
}

export default Site;